/**
 * @fileoverview Letterhead → render-fragment explosion (MOR-953)
 *
 * A letterhead zone can hold MORE THAN ONE image. A firm typically has a
 * full first-page header (logo + name + address) and a lighter header for
 * every page after it. Both live in the same zone, distinguished only by
 * their page rule:
 *
 *   header layer 0: firstOnly → "Page 1 letterhead"
 *   header layer 1: all       → "Page 2 Letter Head"
 *
 * The renderer resolves this by walking an ORDERED list of narrow
 * `VariantFragments` — one per (zone, image, rule) tuple — and taking the
 * first whose zone has content and whose rule includes the page being
 * drawn (`pickVariantForPage`). So every image in a zone must be emitted
 * as its own fragment; collapsing a zone to a single image silently drops
 * the subsequent-page letterhead and leaves pages 2+ bare.
 *
 * This module is the single place that explosion happens. Both the day-one
 * document surface and the Settings > Docs template preview call it, so
 * they cannot drift apart again.
 */

import {
  type PaddingSource,
  type PageRule,
  type SourceBodyMargins,
  type VariantFragments,
  type ZoneKind,
  resolveVariantFragments,
  type LayoutMarginEntry,
} from "./resolve-letterhead";
import { pageFitOffset } from "./pageFit";

const ZONES: readonly ZoneKind[] = [
  "header",
  "footer",
  "leftSide",
  "rightSide",
];

/** A letterhead asset with its image URL already resolved. */
export type LetterheadAssetLike = {
  _id?: string;
  zoneKind?: string | null;
  imageUrl?: string | null;
  imageScale?: number | null;
  defaultPageRule?: PageRule | string | null;
  sideFit?: "fullHeight" | "topBlock" | null;
  topBlockHeightPct?: number | null;
  imageCropTop?: number | null;
  imageCropBottom?: number | null;
  imageCropLeft?: number | null;
  imageCropRight?: number | null;
  /** "page" draws a header/footer at page scale — see `pageFit.ts`. */
  imageFit?: string | null;
  /** Where the crop sits on its source page (fractions of the page). */
  sourceSelection?: { top: number; bottom: number } | null;
  /** Body margins measured from the source page (page-fit header/footer). */
  sourceBodyMargins?: SourceBodyMargins | null;
  bundleName?: string | null;
  sourceDocumentName?: string | null;
};

/** One resolved layer as returned by `variantQueries.listVariantsForOrg`. */
export type ResolvedLayerLike = {
  assetId?: string;
  pageRule?: PageRule | string | null;
  imageUrl?: string | null;
  imageScale?: number | null;
  sideFit?: "fullHeight" | "topBlock" | null;
  topBlockHeightPct?: number | null;
  imageCropTop?: number | null;
  imageCropBottom?: number | null;
  imageCropLeft?: number | null;
  imageCropRight?: number | null;
  /** "page" draws a header/footer at page scale — see `pageFit.ts`. */
  imageFit?: string | null;
  /** Where the crop sits on its source page (fractions of the page). */
  sourceSelection?: { top: number; bottom: number } | null;
  /** Body margins measured from the source page (page-fit header/footer). */
  sourceBodyMargins?: SourceBodyMargins | null;
};

type SideCropBag = {
  top?: number | null;
  bottom?: number | null;
  left?: number | null;
  right?: number | null;
} | null;

/** A resolved variant, layered or legacy single-asset. */
export type LayeredVariantLike = {
  _id?: string;
  resolvedHeaderLayers?: ResolvedLayerLike[] | null;
  resolvedFooterLayers?: ResolvedLayerLike[] | null;
  resolvedLeftSideLayers?: ResolvedLayerLike[] | null;
  resolvedRightSideLayers?: ResolvedLayerLike[] | null;
  headerImageUrl?: string | null;
  footerImageUrl?: string | null;
  leftSideImageUrl?: string | null;
  rightSideImageUrl?: string | null;
  headerImageScale?: number | null;
  footerImageScale?: number | null;
  leftSideImageScale?: number | null;
  rightSideImageScale?: number | null;
  headerPageRule?: PageRule | string | null;
  footerPageRule?: PageRule | string | null;
  leftSidePageRule?: PageRule | string | null;
  rightSidePageRule?: PageRule | string | null;
  leftSideFit?: "fullHeight" | "topBlock" | null;
  rightSideFit?: "fullHeight" | "topBlock" | null;
  leftSideTopBlockHeightPct?: number | null;
  rightSideTopBlockHeightPct?: number | null;
  leftSideCrop?: SideCropBag;
  rightSideCrop?: SideCropBag;
  headerTopPadding?: number | null;
  headerSidePadding?: number | null;
  headerLeftPadding?: number | null;
  headerRightPadding?: number | null;
  bodyTopPadding?: number | null;
  bodySidePadding?: number | null;
  bodyLeftPadding?: number | null;
  bodyRightPadding?: number | null;
  bodyBottomPadding?: number | null;
  footerBottomPadding?: number | null;
  footerSidePadding?: number | null;
  footerLeftPadding?: number | null;
  footerRightPadding?: number | null;
  /** Per-layout margins (see `LayoutMarginEntry`). */
  layoutMargins?: LayoutMarginEntry[] | null;
  /** Legacy single-asset zones (pre-layer variants). */
  headerAssetId?: string | null;
  footerAssetId?: string | null;
  leftSideAssetId?: string | null;
  rightSideAssetId?: string | null;
};

/**
 * Build one narrow fragment carrying a single zone's image + rule. Every
 * other zone is pinned to `"none"` so `pickVariantForPage` skips this
 * fragment when resolving those zones.
 */
function zoneFragment(args: {
  id: string;
  zone: ZoneKind;
  imageUrl: string | null | undefined;
  scale: number | null | undefined;
  pageRule: PageRule | string | null | undefined;
  padding?: PaddingSource | null;
  sideFit?: "fullHeight" | "topBlock" | null;
  topBlockHeightPct?: number | null;
  /**
   * The asset's `imageCrop*` fields. For side zones this is the topBlock
   * source crop; for header/footer it becomes the render-time crop applied
   * inside the image HTML (mirroring the branding ZoneCard preview).
   */
  sideCrop?: SideCropBag;
  /** Asset behind this zone's artwork (for layout-key derivation). */
  assetId?: string | null;
  layoutMargins?: LayoutMarginEntry[] | null;
  /** The asset's fit + source position (header/footer page fit). */
  imageFit?: string | null;
  sourceSelection?: { top: number; bottom: number } | null;
  /** The asset's source-page body margins (header/footer only). */
  sourceBodyMargins?: SourceBodyMargins | null;
}): VariantFragments | null {
  if (!args.imageUrl) return null;
  const { zone } = args;
  const rule = (args.pageRule ?? "all") as PageRule;
  const scale = args.scale ?? 1;
  const assetId = args.assetId ?? null;
  return resolveVariantFragments({
    id: args.id,
    headerAssetId: zone === "header" ? assetId : null,
    footerAssetId: zone === "footer" ? assetId : null,
    leftSideAssetId: zone === "leftSide" ? assetId : null,
    rightSideAssetId: zone === "rightSide" ? assetId : null,
    layoutMargins: args.layoutMargins ?? null,
    headerImageUrl: zone === "header" ? args.imageUrl : null,
    footerImageUrl: zone === "footer" ? args.imageUrl : null,
    leftSideImageUrl: zone === "leftSide" ? args.imageUrl : null,
    rightSideImageUrl: zone === "rightSide" ? args.imageUrl : null,
    headerImageScale: zone === "header" ? scale : 1,
    footerImageScale: zone === "footer" ? scale : 1,
    leftSideImageScale: zone === "leftSide" ? scale : 1,
    rightSideImageScale: zone === "rightSide" ? scale : 1,
    headerPageRule: zone === "header" ? rule : "none",
    footerPageRule: zone === "footer" ? rule : "none",
    leftSidePageRule: zone === "leftSide" ? rule : "none",
    rightSidePageRule: zone === "rightSide" ? rule : "none",
    headerImageCropTop: zone === "header" ? (args.sideCrop?.top ?? null) : null,
    headerImageCropBottom:
      zone === "header" ? (args.sideCrop?.bottom ?? null) : null,
    headerImageCropLeft:
      zone === "header" ? (args.sideCrop?.left ?? null) : null,
    headerImageCropRight:
      zone === "header" ? (args.sideCrop?.right ?? null) : null,
    footerImageCropTop: zone === "footer" ? (args.sideCrop?.top ?? null) : null,
    footerImageCropBottom:
      zone === "footer" ? (args.sideCrop?.bottom ?? null) : null,
    footerImageCropLeft:
      zone === "footer" ? (args.sideCrop?.left ?? null) : null,
    footerImageCropRight:
      zone === "footer" ? (args.sideCrop?.right ?? null) : null,
    leftSideFit: zone === "leftSide" ? (args.sideFit ?? null) : null,
    rightSideFit: zone === "rightSide" ? (args.sideFit ?? null) : null,
    leftSideTopBlockHeightPct:
      zone === "leftSide" ? (args.topBlockHeightPct ?? null) : null,
    rightSideTopBlockHeightPct:
      zone === "rightSide" ? (args.topBlockHeightPct ?? null) : null,
    leftSideCrop: zone === "leftSide" ? (args.sideCrop ?? null) : null,
    rightSideCrop: zone === "rightSide" ? (args.sideCrop ?? null) : null,
    headerPageFitTop:
      zone === "header"
        ? pageFitOffset("header", args.imageFit, args.sourceSelection)
        : null,
    footerPageFitBottom:
      zone === "footer"
        ? pageFitOffset("footer", args.imageFit, args.sourceSelection)
        : null,
    padding: args.padding ?? null,
    sourceBodyMargins:
      zone === "header" || zone === "footer"
        ? (args.sourceBodyMargins ?? null)
        : null,
  });
}

/** "all" (or an absent rule) is the catch-all — it sorts after targeted rules. */
function ruleSpecificity(rule: PageRule | string | null | undefined): number {
  return !rule || rule === "all" ? 1 : 0;
}

/**
 * Explode a letterhead BUNDLE into fragments — one per (zone, asset).
 *
 * A bundle can legitimately hold several assets in one zone (a first-page
 * header alongside an every-other-page header). Page-specific rules are
 * ordered ahead of "all" so a targeted asset wins on the pages it names
 * and the catch-all covers the rest.
 */
export function buildBundleFragments(
  bundleName: string,
  assets: readonly LetterheadAssetLike[],
): VariantFragments[] {
  const bundleAssets = assets.filter(
    (a) => (a.bundleName ?? a.sourceDocumentName ?? null) === bundleName,
  );
  const fragments: VariantFragments[] = [];
  for (const zone of ZONES) {
    const zoneAssets = bundleAssets
      .filter((a) => a.zoneKind === zone && a.imageUrl)
      .sort(
        (x, y) =>
          ruleSpecificity(x.defaultPageRule) -
          ruleSpecificity(y.defaultPageRule),
      );
    for (const a of zoneAssets) {
      const fragment = zoneFragment({
        id: `bundle-${bundleName}-${zone}-${a._id ?? a.imageUrl}`,
        zone,
        assetId: a._id ?? null,
        imageUrl: a.imageUrl,
        scale: a.imageScale ?? null,
        pageRule: a.defaultPageRule ?? "all",
        sideFit: a.sideFit ?? null,
        topBlockHeightPct: a.topBlockHeightPct ?? null,
        sideCrop: {
          top: a.imageCropTop ?? null,
          bottom: a.imageCropBottom ?? null,
          left: a.imageCropLeft ?? null,
          right: a.imageCropRight ?? null,
        },
        imageFit: a.imageFit ?? null,
        sourceSelection: a.sourceSelection ?? null,
        sourceBodyMargins: a.sourceBodyMargins ?? null,
      });
      if (fragment) fragments.push(fragment);
    }
  }
  return fragments;
}

/**
 * Explode a letterhead VARIANT into fragments — one per (zone, layer),
 * in the order the user arranged them.
 *
 * Variants created before the layer arrays landed carry a single image
 * per zone in legacy `*ImageUrl` / `*PageRule` fields; those still render
 * as a one-fragment zone.
 */
export function buildVariantFragments(
  variant: LayeredVariantLike,
): VariantFragments[] {
  const padding: PaddingSource = {
    headerTopPadding: variant.headerTopPadding,
    headerSidePadding: variant.headerSidePadding,
    headerLeftPadding: variant.headerLeftPadding,
    headerRightPadding: variant.headerRightPadding,
    bodyTopPadding: variant.bodyTopPadding,
    bodySidePadding: variant.bodySidePadding,
    bodyLeftPadding: variant.bodyLeftPadding,
    bodyRightPadding: variant.bodyRightPadding,
    bodyBottomPadding: variant.bodyBottomPadding,
    footerBottomPadding: variant.footerBottomPadding,
    footerSidePadding: variant.footerSidePadding,
    footerLeftPadding: variant.footerLeftPadding,
    footerRightPadding: variant.footerRightPadding,
  };

  const zoneSpecs: Array<{
    zone: ZoneKind;
    layers: ResolvedLayerLike[] | null | undefined;
    legacyImageUrl: string | null | undefined;
    legacyScale: number | null | undefined;
    legacyPageRule: PageRule | string | null | undefined;
    legacyAssetId?: string | null;
    legacySideFit?: "fullHeight" | "topBlock" | null;
    legacyTopBlockHeightPct?: number | null;
    legacySideCrop?: SideCropBag;
  }> = [
    {
      zone: "header",
      layers: variant.resolvedHeaderLayers,
      legacyImageUrl: variant.headerImageUrl,
      legacyScale: variant.headerImageScale,
      legacyPageRule: variant.headerPageRule,
      legacyAssetId: variant.headerAssetId ?? null,
    },
    {
      zone: "footer",
      layers: variant.resolvedFooterLayers,
      legacyImageUrl: variant.footerImageUrl,
      legacyScale: variant.footerImageScale,
      legacyPageRule: variant.footerPageRule,
      legacyAssetId: variant.footerAssetId ?? null,
    },
    {
      zone: "leftSide",
      layers: variant.resolvedLeftSideLayers,
      legacyImageUrl: variant.leftSideImageUrl,
      legacyScale: variant.leftSideImageScale,
      legacyPageRule: variant.leftSidePageRule,
      legacyAssetId: variant.leftSideAssetId ?? null,
      legacySideFit: variant.leftSideFit ?? null,
      legacyTopBlockHeightPct: variant.leftSideTopBlockHeightPct ?? null,
      legacySideCrop: variant.leftSideCrop ?? null,
    },
    {
      zone: "rightSide",
      layers: variant.resolvedRightSideLayers,
      legacyImageUrl: variant.rightSideImageUrl,
      legacyScale: variant.rightSideImageScale,
      legacyPageRule: variant.rightSidePageRule,
      legacyAssetId: variant.rightSideAssetId ?? null,
      legacySideFit: variant.rightSideFit ?? null,
      legacyTopBlockHeightPct: variant.rightSideTopBlockHeightPct ?? null,
      legacySideCrop: variant.rightSideCrop ?? null,
    },
  ];

  const fragments: VariantFragments[] = [];
  for (const spec of zoneSpecs) {
    if (spec.layers && spec.layers.length > 0) {
      spec.layers.forEach((layer, i) => {
        const fragment = zoneFragment({
          id: `${variant._id ?? "variant"}-${spec.zone}-${i}`,
          zone: spec.zone,
          imageUrl: layer.imageUrl,
          scale: layer.imageScale,
          pageRule: layer.pageRule,
          padding,
          assetId: layer.assetId ?? null,
          layoutMargins: variant.layoutMargins ?? null,
          sideFit: layer.sideFit ?? null,
          topBlockHeightPct: layer.topBlockHeightPct ?? null,
          sideCrop: {
            top: layer.imageCropTop ?? null,
            bottom: layer.imageCropBottom ?? null,
            left: layer.imageCropLeft ?? null,
            right: layer.imageCropRight ?? null,
          },
          imageFit: layer.imageFit ?? null,
          sourceSelection: layer.sourceSelection ?? null,
          sourceBodyMargins: layer.sourceBodyMargins ?? null,
        });
        if (fragment) fragments.push(fragment);
      });
      continue;
    }
    const fragment = zoneFragment({
      id: `${variant._id ?? "variant"}-${spec.zone}-legacy`,
      zone: spec.zone,
      imageUrl: spec.legacyImageUrl,
      scale: spec.legacyScale,
      pageRule: spec.legacyPageRule ?? "all",
      padding,
      assetId: spec.legacyAssetId ?? null,
      layoutMargins: variant.layoutMargins ?? null,
      sideFit: spec.legacySideFit ?? null,
      topBlockHeightPct: spec.legacyTopBlockHeightPct ?? null,
      sideCrop: spec.legacySideCrop ?? null,
    });
    if (fragment) fragments.push(fragment);
  }
  return fragments;
}

/** A resolved letterhead choice, as produced by `resolveLetterheadForDoc`. */
export type ResolvedLetterheadLike =
  | { kind: "none" }
  | { kind: "letterhead"; bundleName: string }
  | { kind: "variant"; variant: LayeredVariantLike };

/**
 * Explode whichever letterhead won resolution into render fragments.
 * Returns `[]` when the document should carry no letterhead at all.
 */
export function buildLetterheadFragments(
  resolved: ResolvedLetterheadLike | null | undefined,
  assets: readonly LetterheadAssetLike[],
): VariantFragments[] {
  if (!resolved || resolved.kind === "none") return [];
  if (resolved.kind === "letterhead") {
    return buildBundleFragments(resolved.bundleName, assets);
  }
  return buildVariantFragments(resolved.variant);
}
